import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { postsService, FeedPost, FeedComment } from '../../services/postsService';
import { usersService } from '../../services/usersService';
import UserAvatar from '../UserAvatar';
import EchobotFeedAvatar from '../Echobot/EchobotFeedAvatar';
import { 
  MessageSquare, 
  User, 
  Calendar,
  Target,
  Building2,
  CheckCircle,
  ChevronRight,
  Activity,
  TrendingUp,
  MessageCircle,
  Clock,
  Plus,
  Send,
  Loader2,
  Sparkles
} from 'lucide-react';

interface DashboardFeedProps {
  orgId: string;
  currentUserId?: string;
  currentUserName?: string;
  currentUserPhoto?: string;
  maxPosts?: number;
  onViewAll?: () => void;
}

const DashboardFeed: React.FC<DashboardFeedProps> = ({ 
  orgId,
  currentUserId,
  currentUserName,
  currentUserPhoto,
  maxPosts = 5,
  onViewAll
}) => {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsMap, setCommentsMap] = useState<Record<string, number>>({});
  const [quickPost, setQuickPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const unsub = postsService.onFeed(orgId, async (items) => {
      // Limit to most recent posts for dashboard
      const recentPosts = items.slice(0, maxPosts);
      setPosts(recentPosts);
      setLoading(false);
      
      // Get comment counts
      const counts: Record<string, number> = {};
      recentPosts.forEach(post => {
        counts[post.id] = post.commentsCount || 0;
      });
      setCommentsMap(counts);

      // Load user avatars
      const userIds = [...new Set(recentPosts.map(p => p.createdById))];
      const avatars: Record<string, string> = {};
      
      for (const userId of userIds) {
        try {
          const users = await usersService.getUsers();
          const user = users.find(u => u.id === userId);
          if (user?.avatar) {
            avatars[userId] = user.avatar;
          }
        } catch (error) {
          console.error('Error loading user avatar:', error);
        }
      }
      setUserAvatars(avatars);
    }, (error) => {
      console.error('Dashboard feed error:', error);
      setLoading(false);
    });

    return unsub;
  }, [orgId, maxPosts]);

  const getEntityIcon = (entityType?: string) => {
    switch (entityType) {
      case 'lead': return <User className="w-3 h-3" />;
      case 'opportunity': return <Target className="w-3 h-3" />;
      case 'customer': return <Building2 className="w-3 h-3" />;
      case 'task': return <CheckCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('feed.justNow');
    if (diffMins < 60) return t('feed.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('feed.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('feed.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const getActivityTypeText = (post: FeedPost) => {
    if (post.type === 'post') return 'posted an update';
    if (post.type === 'lead_created') return 'created a new lead';
    if (post.type === 'opportunity_created') return 'created a new opportunity';
    if (post.type === 'task_created') return 'created a new task';
    if (post.type === 'lead_stage_changed') return 'updated lead stage';
    if (post.type === 'opportunity_stage_changed') return 'updated opportunity stage';
    if (post.type === 'echobot_tip') return 'shared a tip';
    if (post.type === 'echobot_announcement') return 'has an announcement';
    if (post.type === 'echobot_help') return 'offers help';
    if (post.type === 'echobot_milestone') return 'celebrates a milestone';
    return 'added activity';
  };

  const handleQuickPost = async () => {
    if (!quickPost.trim() || !currentUserId || !currentUserName) return;
    
    setPosting(true);
    try {
      await postsService.createUserPost({
        orgId,
        content: quickPost.trim(),
        createdById: currentUserId,
        createdByName: currentUserName
      });
      setQuickPost('');
      setShowQuickPost(false);
    } catch (error) {
      console.error('Failed to create quick post:', error);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 h-full flex flex-col hover:shadow-card transition-shadow duration-200">
        <div className="flex items-center space-x-2 mb-6 flex-shrink-0">
          <div className="p-2 bg-info-soft rounded-lg">
            <Activity className="w-5 h-5 text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-txt">{t('feed.title')}</h3>
        </div>
        <div className="flex items-center justify-center flex-1">
          <Clock className="w-6 h-6 animate-spin text-info" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 h-full flex flex-col p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-info-soft rounded-lg">
            <Activity className="w-5 h-5 text-accent" />
          </div>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-txt">{t('feed.title')}</h3>
            <span className="text-xs text-txt-secondary bg-bg-surface px-2 py-1 rounded-full">
              {posts.length} {tc('new')}
            </span>
          </div>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="flex items-center text-sm text-accent hover:text-accent font-medium transition-colors"
          >
            {t('feed.viewAll')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>

      <div className="space-y-4 flex-1 overflow-y-auto">
        {/* Quick Post */}
        {currentUserId && currentUserName && (
          <div className="pb-4 border-b border-subtle">
            {showQuickPost ? (
              <div className="space-y-3">
                <textarea
                  value={quickPost}
                  onChange={(e) => setQuickPost(e.target.value)}
                  placeholder="Share a quick update..."
                  className="w-full px-3 py-2 border-0 bg-bg-surface rounded-lg focus:ring-2 focus:ring-accent/30 focus:bg-bg-card resize-none text-sm"
                  rows={3}
                  disabled={posting}
                  autoFocus
                />
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowQuickPost(false);
                      setQuickPost('');
                    }}
                    className="px-3 py-1.5 text-sm text-txt-secondary hover:text-txt-secondary transition-colors"
                    disabled={posting}
                  >
                    {tc('cancel')}
                  </button>
                  <button
                    onClick={handleQuickPost}
                    disabled={posting || !quickPost.trim()}
                    className="px-3 py-1.5 bg-primary text-primary-contrast rounded-lg hover:bg-bg-card disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center text-sm"
                  >
                    {posting ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                        {tc('loading')}
                      </>
                    ) : (
                      <>
                        <Send className="w-3 h-3 mr-1.5" />
                        Post
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowQuickPost(true)}
                className="w-full px-3 py-2 text-left text-sm text-txt-tertiary bg-bg-surface rounded-lg hover:bg-bg-surface transition-colors flex items-center"
              >
                <Plus className="w-3 h-3 mr-2" />
                Share an update...
              </button>
            )}
          </div>
        )}
        
        {/* Posts List */}
        <div>
          {posts.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-10 h-10 text-txt-muted mx-auto mb-3" />
              <p className="text-txt-tertiary text-sm">{t('feed.noActivity')}</p>
              <p className="text-xs text-txt-muted mt-1">Updates will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => {
                const isEchobotPost = post.createdById === 'echobot';
                return (
              <div
                key={post.id}
                className={`group hover:bg-bg-surface -mx-4 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                  isEchobotPost ? 'border-l-4 border-accent bg-gradient-to-r from-accent/50 to-transparent' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  {isEchobotPost ? (
                    <EchobotFeedAvatar size="small" animated />
                  ) : (
                    <UserAvatar 
                      photoURL={userAvatars[post.createdById] || (post.createdById === currentUserId ? currentUserPhoto : undefined)}
                      name={post.createdByName}
                      size="sm"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className={`text-sm font-medium ${isEchobotPost ? 'text-accent' : 'text-txt'}`}>
                        {post.createdByName}
                      </p>
                      {isEchobotPost && <Sparkles className="w-3 h-3 text-accent" />}
                      <span className="text-xs text-txt-tertiary">
                        {getActivityTypeText(post)}
                      </span>
                    </div>
                    
                    {(post.type === 'post' || post.type.startsWith('echobot_')) && post.content && (
                      <p className={`text-sm mt-1 line-clamp-2 ${isEchobotPost ? 'text-accent font-medium' : 'text-txt-secondary'}`}>
                        {post.content}
                      </p>
                    )}
                    
                    {post.entityLabel && (
                      <div className="mt-1">
                        <span className="inline-flex items-center space-x-1 text-xs bg-bg-surface text-txt-secondary px-2 py-1 rounded">
                          {getEntityIcon(post.entityType)}
                          <span>{post.entityLabel}</span>
                        </span>
                      </div>
                    )}
                    
                    {/* Action button for Echobot posts */}
                    {isEchobotPost && post.echobotActionButton && (
                      <div className="mt-3">
                        <button
                          onClick={() => {
                            // Handle action - you can add onActionClick prop to DashboardFeed if needed
                            console.log('Echobot action:', post.echobotActionButton?.action);
                          }}
                          className="inline-flex items-center px-3 py-1.5 bg-accent hover:bg-accent text-txt-inverted text-xs font-medium rounded-lg transition-colors shadow-card"
                        >
                          {post.echobotActionButton.label}
                          <ChevronRight className="w-3 h-3 ml-1" />
                        </button>
                      </div>
                    )}

                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-xs text-txt-tertiary">
                        {formatTimeAgo(new Date(post.createdAt))}
                      </span>
                      {commentsMap[post.id] > 0 && (
                        <span className="flex items-center text-xs text-txt-tertiary">
                          <MessageCircle className="w-3 h-3 mr-1" />
                          {commentsMap[post.id]}
                        </span>
                      )}
                      {post.attachments && post.attachments.length > 0 && (
                        <span className="text-xs text-txt-tertiary">
                          📎 {post.attachments.length} file{post.attachments.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardFeed;
